import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Search,
  Plus,
  Send,
  ArrowLeft,
  Minus,
  X,
  SearchIcon,
  ChevronDown,
  Lock,
  User,
  Palette,
  Smile,
  Tag,
  Pin,
  Users,
  Pencil
} from "lucide-react";
import {
  Box,
  Typography,
  Avatar,
  Badge,
  TextField,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Autocomplete,
  FormGroup,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment
} from "@mui/material";
import dayjs from "dayjs";
import variableStyles from "../../theme/variable-styles";
import { jwtDecode } from "jwt-decode";
import { ITag, ITagReq, ITokenPayload } from "../../models/user";
import { ControlForm, Modal } from "../../components";
import InputForm from "../../components/controls/InputForm";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import EditNameGroupChat from "./modal/EditNameGroupChat";
import { getSignalConnection } from "../../services/api/signalr.Service";
import ConversationService from "../../services/api/conversation.service";
import {
  IConversationReq,
  ICreateConversationReq,
  IGetPageConversationReq,
  IMarkAsReadReq,
  IMessageAttachmentFileRequest,
  IMessagesReq,
  IMessagesRs,
  IPageConversationRs,
  IUpdateGroupTitleReq,
  TypeConversation
} from "../../models/conversation";
import { TagInput } from "../WorkflowManagement/components/TagInput/TagInput";
import { IBasePagingRes } from "../../models/common/models.type";
import { TagType } from "../../models/common/models.enum";
import CustomChatTabPanel from "../Chat/CustomChatTabPanel";
import UserService from "../../services/api/user.service";
import { set } from "lodash";
import {
  AttachFile,
  Close,
  InsertEmoticon,
  PictureAsPdf
} from "@mui/icons-material";
import EmojiPicker from "emoji-picker-react";
import UploadFileService from "../../services/api/uploadFile.service";
import { toggleMessage } from "../../components/Toast/Toast";
import { envConfig } from "../../config/env.config";
import { useTranslation } from "react-i18next";
import RenderMentions from "./RenderMentions";

// ======================== STYLED COMPONENTS ========================

export interface DataForm {
  receiver: ITag[];
}

export const Wrapper = styled("div")(() => ({
  gap: "1rem",
  flexDirection: "row-reverse",
  position: "absolute",
  bottom: "0",
  right: "0",
  //width: expanded ? 320 : 320,
  //height: 400,
  display: "flex",
  alignItems: "end",
  zIndex: "10"
}));

export const Sidebar = styled("div")(() => ({
  overflow: "hidden",
  borderRadius: "8px 8px 0 0",
  transition: "width 0.3s ease",
  backgroundColor: "#fff",
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  border: "1px solid #ddd",
  width: 280,
  borderRight: "1px solid #eee",
  display: "flex",
  flexDirection: "column"
}));

export const Header = styled("div")(() => ({
  backgroundColor: variableStyles.MainColor,
  color: "#fff",
  padding: "10px 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontWeight: 600,
  fontSize: 14
}));

export const TabBar = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #eee",
  padding: "8px 14px",
  fontSize: 13
}));

export const OnlineBox = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  color: "green"
}));

export const UserList = styled("div")(() => ({
  flex: 1,
  overflowY: "auto",
  height: "300px"
}));

export const UserItem = styled("div")<{ selected?: boolean }>(
  ({ selected }) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f1f1",
    backgroundColor: selected ? "#f8f8f8" : "transparent",
    "&:hover": {
      backgroundColor: "#f9f9f9"
    }
  })
);

export const AvatarBox = styled("div")(
  ({
    src,
    width,
    height
  }: {
    src?: string;
    width?: number;
    height: number;
  }) => ({
    width: width,
    height: height,
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    backgroundImage: src ? `url(${src})` : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    textTransform: "uppercase",
    overflow: "hidden"
  })
);

export const MessageBox = styled("div")(() => ({
  flex: 1,
  overflow: "hidden"
}));

// CHAT PANEL
export const ChatPanel = styled("div")(() => ({
  //overflow: "hidden",
  borderRadius: "8px 8px 0 0",
  // backgroundColor: "#fff",
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  border: "1px solid #ddd",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fafafa"
  // minHeight: "383px"
}));

export const ChatHeader = styled("div")(() => ({
  padding: "10px 14px",
  borderBottom: "1px solid #ddd",
  fontWeight: 600,
  //display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "8px 8px 0 0"
}));

export const ChatMessages = styled("div")(() => ({
  flex: 1,
  overflowY: "auto",
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  //gap: 8,
  //width: "290px",
  minHeight: "295px"
}));

export const ChatInputBox = styled("div")(() => ({
  borderTop: "1px solid #ddd",
  display: "flex",
  alignItems: "center",
  backgroundColor: "#fff"
}));

// ======================== COMPONENT ========================

const ChatSidebar: React.FC = () => {
  const [openEmojiFor, setOpenEmojiFor] = useState<string | null>(null);

  const emojiInputRef = useRef<HTMLDivElement | null>(null);
  const emojiPopupRef = useRef<HTMLDivElement | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const mentionRef = useRef<HTMLDivElement | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0); // item đang chọn bằng phím
  const [cursorPos, setCursorPos] = useState(0);

  const { t } = useTranslation();

  const messagesRef = useRef<Record<string, HTMLDivElement | null>>({});

  const [pageSizeConversation, setPageSizeConversation] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [tabValue, setTabValue] = React.useState(0);

  const [token, setToken] = useState<ITokenPayload>();
  const [companyName, setCompanyName] = useState<string>();

  const [conversation, setConversation] =
    useState<IBasePagingRes<IPageConversationRs>>();

  const [userChats, setUserChats] = useState<IBasePagingRes<ITag>>();

  const [totalUserOnline, setTotalUserOnline] = useState<number>(0);

  //const [selectedUser, setSelectedUser] = useState<ChatUser | null>();
  const [selectedListUser, setSelectedListUser] = useState<IConversationReq[]>(
    []
  );

  //const [input, setInput] = useState("");
  const [isOpenMessage, setIsOpenMessage] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isOpenModelEditNameGroupChat, setIsOpenModelEditNameGroupChat] =
    useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [dataUpdateTitle, setDataUpdateTitle] =
    useState<IUpdateGroupTitleReq>();

  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    conversationId: string | null;
  }>({ anchorEl: null, conversationId: null });

  const handleClick = (
    event: MouseEvent<HTMLElement>,
    conversationId: string
  ) => {
    setMenuState({ anchorEl: event.currentTarget, conversationId });
  };

  const scrollToBottom = (groupId: string) => {
    const el = messagesRef.current[groupId];
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };
  const handleCloseMenuMessage = () => {
    setMenuState({ anchorEl: null, conversationId: null });
  };

  const handleInputChange = async (groupId: string, value?: string) => {
    setSelectedListUser(prev =>
      prev.map(g => (g.id === groupId ? { ...g, text: value } : g))
    );

    const gr = selectedListUser.find(g => g.id === groupId);
    if (!gr || !value) {
      setShowMentionList(false);
      return;
    }

    // ❗️Chỉ cho @ trong group
    if (gr.type !== TypeConversation.Group) {
      setShowMentionList(false);
      return;
    }

    // Vị trí con trỏ để xác định query sau '@'
    const inputEl = inputRef.current;
    const caret = inputEl?.selectionStart || value.length;
    setCursorPos(caret);

    // Tìm ký tự '@' gần nhất trước caret
    const lastAt = value.lastIndexOf("@", caret - 1);

    if (lastAt !== -1) {
      // Query là đoạn giữa '@' và caret
      const query = value.slice(lastAt + 1, caret);
      setMentionQuery(query); // query có thể rỗng → show all
      setShowMentionList(true);
      setMentionIndex(0); // reset index khi gõ lại
    } else {
      setShowMentionList(false);
    }
  };

  const handleInputFocus = async (groupId: string) => {
    const group = selectedListUser.find(g => g.id === groupId);
    if (!group || !token?.userId) return;

    const lastMessage = group.messages?.[group.messages.length - 1];
    if (!lastMessage) return;

    if (lastMessage.senderId === token.userId) return;

    const markAsRead: IMarkAsReadReq = {
      ConversationId: groupId,
      LastMessageId: lastMessage.id,
      UserId: token.userId
    };

    try {
      await ConversationService.markAsRead(markAsRead);

      // ✅ 1. Reset unreadCount trong sidebar (conversation list)
      setConversation(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(conv =>
            conv.id === groupId ? { ...conv, unreadCount: 0 } : conv
          )
        };
      });

      // ✅ 2. Reset unreadCount trong panel đang mở
      setSelectedListUser(prev =>
        prev.map(conv =>
          conv.id === groupId ? { ...conv, unreadCount: 0 } : conv
        )
      );
    } catch (error) {
      // có thể log nếu cần
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode<ITokenPayload>(token);

      setToken(decoded);
    }
  }, []);

  // Escape tên cho an toàn regex
  const escapeRegExp = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const getMentionedUserIds = (
    text: string | undefined,
    participants: { id: string; fullName?: string; userName?: string }[] = []
  ): string[] => {
    if (!text) return [];

    const result = new Set<string>();

    participants.forEach(p => {
      if (!p.fullName) return;

      // pattern: @Full Name (cuối chuỗi hoặc trước khoảng trắng / dấu câu)
      const pattern = new RegExp(
        `@${escapeRegExp(p.fullName)}(\\s|$|[,.!?])`,
        "i"
      );

      if (pattern.test(text)) {
        result.add(p.id);
      }
    });

    return Array.from(result);
  };

  const sameMembers = (a: string[] = [], b: string[] = []) =>
    a.length === b.length && a.every(x => b.includes(x));

  const handleSend = async (group: IConversationReq, input?: string) => {
    if (
      !input?.trim() &&
      (!group.selectedFiles || group.selectedFiles.length === 0)
    )
      return;

    let conversationId: string | undefined = group.id;
    const userId = token?.userId;
    let messageAttachments: IMessageAttachmentFileRequest[] = [];

    if (group.selectedFiles && group.selectedFiles.length > 0) {
      const formData = new FormData();
      formData.append("FolderName", "User");
      (group.selectedFiles || []).forEach(file => {
        formData.append("Files", file); // 👈 thêm từng file
      });
      await UploadFileService.uploadFile(formData).then(response => {
        if (response.success) {
          response.data.map(p => {
            let messageAttachment: IMessageAttachmentFileRequest = {
              fileName: p.fileName,
              contentType: p.contentType,
              path: p.path,
              extension: p.extension
            };
            messageAttachments.push(messageAttachment);
          });
        } else {
          toggleMessage({ type: "error", message: response?.error ?? "" });
          return;
        }
      });
    }
    // ... upload files giữ nguyên

    // ✅ Nếu đang ở khung "New chat", kiểm tra xem chat Direct đã tồn tại chưa
    if (group.isCreateGroupChat) {
      const me = userId;
      const target = [...(group.participantIds || [])];
      if (me && !target.includes(me)) target.push(me);

      const existed = conversation?.items?.find(
        c =>
          c.type === TypeConversation.Direct &&
          sameMembers(c.participantIds || [], target)
      );

      if (existed) {
        // 👉 Dùng luôn conversation cũ + nạp lịch sử vào khung đang mở
        conversationId = existed.id;

        // nạp lịch sử để không chỉ thấy 1 tin
        const page: IGetPageConversationReq = {
          conversationId: existed.id,
          currentPage: 1,
          pageSize: group.pageSize || 20
        };
        const res = await ConversationService.getPageMessages(page);
        const history = res?.items?.reverse() || [];

        setSelectedListUser(prev =>
          prev.map(g =>
            g.id === group.id
              ? {
                  ...existed,
                  isOpenMessage: true,
                  isCreateGroupChat: false,
                  messages: history,
                  text: group.text, // giữ nội dung đang gõ
                  selectedFiles: group.selectedFiles // giữ file vừa chọn
                }
              : g
          )
        );
      } else {
        // ❗ Chưa có: tạo mới như cũ, nhưng nhớ nạp lịch sử ngay sau khi tạo
        if (userId && !group.participantIds.includes(userId)) {
          group.participantIds.push(userId);
        }
        const payload: ICreateConversationReq = {
          title: group.title,
          messageContent: group.text || "",
          type:
            group.participantIds.length <= 2
              ? TypeConversation.Direct
              : TypeConversation.Group,
          participantIds: group.participantIds,
          attachments: messageAttachments
        };

        const created = await ConversationService.create(payload);
        if (created) {
          conversationId = created.id;
          await loadData(10); // refresh list

          // 🔁 nạp lịch sử ngay để không bị "chỉ 1 tin"
          const page: IGetPageConversationReq = {
            conversationId: created.id,
            currentPage: 1,
            pageSize: group.pageSize || 20
          };
          const res = await ConversationService.getPageMessages(page);
          const history = res?.items?.reverse() || [];

          setSelectedListUser(prev =>
            prev.map(g =>
              g.id === group.id
                ? {
                    ...created,
                    isOpenMessage: true,
                    isCreateGroupChat: false,
                    messages: history,
                    text: group.text,
                    selectedFiles: group.selectedFiles
                  }
                : g
            )
          );
        }
      }
    }
    const mentionedUserIds =
      group.type === TypeConversation.Group
        ? getMentionedUserIds(input ?? "", group.participants || [])
        : [];
    // ===== Gửi tin nhắn như cũ, nhưng dùng conversationId đã xác định ở trên =====
    const newMessage: IMessagesReq = {
      conversationId,
      senderId: userId,
      content: input ?? "",
      attachments: messageAttachments,
      sendFireBaseMessenger: {
        titleGroup: group.title,
        mentionedUserIds: mentionedUserIds
      }
    };

    const connection = getSignalConnection();
    if (!connection) return;
    try {
      await connection.invoke("SendMessage", newMessage);
    } catch (e) {}

    // reset input & files
    setSelectedListUser(prev =>
      prev.map(g =>
        g.id === group.id || g.id === conversationId
          ? { ...g, text: "", selectedFiles: [] }
          : g
      )
    );

    setTimeout(() => conversationId && scrollToBottom(conversationId), 10);
  };

  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      const t = e.target as Node;
      const insideInput = !!emojiInputRef.current?.contains(t);
      const insidePopup = !!emojiPopupRef.current?.contains(t);
      const onToggleBtn = (t as HTMLElement).closest?.("[data-emoji-toggle]");

      // chỉ đóng khi không bấm vào input, không bấm vào popup, và không bấm nút toggle
      if (!insideInput && !insidePopup && !onToggleBtn) setOpenEmojiFor(null);
    };

    if (openEmojiFor)
      document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [openEmojiFor]);

  useEffect(() => {
    if (!token) return;

    const connection = getSignalConnection();
    if (!connection) {
      console.error("❌ No SignalR connection available");
      return;
    }
    const myUserId = token?.userId;

    const handleReceiveMessage = (data: IMessagesRs) => {
      const myUserId = token?.userId;

      const isMyMessage =
        data.senderId === myUserId || data.sender?.id === myUserId;

      // ✅ 1. Cập nhật list conversation bên trái
      setConversation(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          items: prev.items.map(conv => {
            if (conv.id !== data.conversationId) return conv;

            return {
              ...conv,
              unreadCount: isMyMessage
                ? conv.unreadCount || 0
                : (conv.unreadCount || 0) + 1
            };
          })
        };
      });

      // ✅ 2. Cập nhật các panel chat đang mở
      setSelectedListUser(prev =>
        prev.map(g => {
          if (g.id !== data.conversationId) return g;

          return {
            ...g,
            unreadCount: isMyMessage
              ? g.unreadCount || 0
              : (g.unreadCount || 0) + 1, // ❌ bỏ điều kiện isCurrentOpen
            messages: [...(g.messages || []), data]
          };
        })
      );
    };

    connection.on("AccountConnected", userId => {
      setConversation(prev =>
        prev
          ? {
              ...prev,
              items: prev.items.map(p =>
                p.participantIds?.includes(userId) ? { ...p, online: true } : p
              )
            }
          : prev
      );

      setSelectedListUser(prev =>
        prev.map(p =>
          p.participantIds?.includes(userId) ? { ...p, online: true } : p
        )
      );
    });

    connection.on("AccountDisconnected", userId => {
      setConversation(prev =>
        prev
          ? {
              ...prev,
              items: prev.items.map(p =>
                p.participantIds?.includes(userId) ? { ...p, online: false } : p
              )
            }
          : prev
      );

      setSelectedListUser(prev =>
        prev.map(p =>
          p.participantIds?.includes(userId) ? { ...p, online: false } : p
        )
      );
    });

    connection.on("GroupRenamed", data => {
      setConversation(prev =>
        prev
          ? {
              ...prev,
              items: prev.items.map(p =>
                p.id === data.conversationId
                  ? { ...p, title: data.newTitle }
                  : p
              )
            }
          : prev
      );

      setSelectedListUser(prev =>
        prev.map(p =>
          p.id === data.conversationId ? { ...p, title: data.newTitle } : p
        )
      );
    });

    connection.on("UserRead", data => {
      // ❌ KHÔNG đụng vào unreadCount nữa, chỉ update seenBy

      setSelectedListUser(prev =>
        prev.map(conv =>
          conv.id === data.conversationId
            ? {
                ...conv,
                messages: conv.messages?.map(msg =>
                  msg.id === data.lastMessageId
                    ? { ...msg, seenBy: data.userSeenBy }
                    : msg
                )
              }
            : conv
        )
      );
    });

    connection.on("ConversationCreated", (conv: IPageConversationRs) => {
      setConversation(prev => {
        // ❌ chưa có dữ liệu trước đó → tạo mới đúng theo ApiPageList
        if (!prev) {
          return {
            currentPage: 1,
            totalPages: 1,
            pageSize: 10, // tùy pageSize bạn đang xài
            totalCount: 1,
            items: [conv],
            message: null,
            statusCode: 200, // hoặc EnumStatusCodesResult.Success nếu có
            success: true,
            error: null
          };
        }

        // ✅ đã có list -> prepend conversation mới
        const newTotalCount = prev.totalCount + 1;
        return {
          ...prev,
          items: [conv, ...prev.items],
          totalCount: newTotalCount,
          totalPages: Math.max(
            prev.totalPages,
            Math.ceil(newTotalCount / prev.pageSize)
          )
        };
      });
    });

    connection.on("ReceiveMessage", handleReceiveMessage);

    return () => {
      connection.off("ReceiveMessage", handleReceiveMessage);
    };
  }, [token]); // ✅ chỉ chạy 1 lần khi component mount

  async function loadData(newPageSize: number) {
    if (isLoading || !hasMore) return;
    setIsLoading(true);

    const page: IGetPageConversationReq = {
      currentPage: 1,
      pageSize: newPageSize
    };

    const getCoversatitonPage =
      await ConversationService.getPageConversationsByUser(page);

    const newItems = getCoversatitonPage.items || [];
    const noMoreData = newItems.length < newPageSize;

    const getOnlineUsersRelatedToUserAsync =
      await ConversationService.getOnlineUsersRelatedToUserAsync();

    setTotalUserOnline(
      getOnlineUsersRelatedToUserAsync.data.onlineRelatedUsers
    );

    setCompanyName(getOnlineUsersRelatedToUserAsync.data.companyName);
    setConversation(getCoversatitonPage);
    setPageSizeConversation(newPageSize);
    setHasMore(!noMoreData);
    setIsLoading(false);
  }

  useEffect(() => {
    loadData(pageSizeConversation);
  }, []);
  // const handleBack = () => setSelectedUser(null);

  const handSelectedConversation = async (conversation: IConversationReq) => {
    const indexUser = selectedListUser.findIndex(p => p.id === conversation.id);
    if (indexUser === -1) {
      conversation.isOpenMessage = true;

      const page: IGetPageConversationReq = {
        conversationId: conversation.id,
        currentPage: 1,
        pageSize: 10
      };
      const getPageMessages = await ConversationService.getPageMessages(page);

      conversation.messages = getPageMessages.items.reverse();

      let newSelectedListUser = [...selectedListUser, conversation];

      let found = false;

      if (selectedListUser.length > 2) {
        newSelectedListUser = newSelectedListUser.map((p, index) => {
          if (!found && p.isHiddenChat !== true) {
            found = true;
            return { ...p, isHiddenChat: true }; // Gán cho phần tử đầu tiên chưa bị ẩn
          }
          return p;
        });
      }
      setSelectedListUser(newSelectedListUser);
    } else {
      let newSelectedListUser = [...selectedListUser];

      const userHidden =
        newSelectedListUser[
          newSelectedListUser.findIndex(p => p.id === conversation.id)
        ]?.isHiddenChat;

      if (userHidden) {
        if (selectedListUser.length > 2) {
          let found = false;

          newSelectedListUser = newSelectedListUser.map(p => {
            // 👉 Nếu là user bị click và đang bị ẩn → bỏ ẩn
            if (p.id === conversation.id && p.isHiddenChat === true) {
              return { ...p, isHiddenChat: false };
            }

            // 👉 Tìm phần tử đầu tiên chưa bị ẩn → ẩn nó
            if (!found && p.isHiddenChat !== true && p.id !== conversation.id) {
              found = true;
              return { ...p, isHiddenChat: true };
            }

            return p;
          });

          setSelectedListUser(newSelectedListUser);
        }
      }
    }
    setTimeout(() => conversation.id && scrollToBottom(conversation.id), 10);
  };

  const handCreateGroupChat = async (
    type: TypeConversation,
    reciver?: ITag,
    isUser?: boolean
  ) => {
    // ===== 1. Nếu là GROUP (TagType.Conversation) và đã mở rồi thì chỉ mở lại, không tạo thêm =====
    if (reciver?.type === TagType.Conversation && reciver.id) {
      const existed = selectedListUser.find(p => p.id === reciver.id);

      if (existed) {
        // 👉 Chỉ show lại panel này, bỏ ẩn, và nếu >2 panel thì ẩn bớt 1 panel khác
        setSelectedListUser(prev => {
          let foundToHide = false;

          const next = prev.map(p => {
            if (p.id === reciver.id) {
              // mở lại panel group đang click
              return { ...p, isHiddenChat: false, isOpenMessage: true };
            }

            // Nếu đang có hơn 2 panel thì ẩn bớt 1 panel khác (giống logic cũ)
            if (prev.length > 2 && !foundToHide && p.isHiddenChat !== true) {
              foundToHide = true;
              return { ...p, isHiddenChat: true };
            }

            return p;
          });

          return next;
        });

        setTimeout(() => scrollToBottom(reciver.id), 10);
        handleCloseMenuMessage();
        return; // ❗ Dừng ở đây, không tạo thêm group nữa
      }
    }

    // ===== 2. Chặn tạo thêm "new chat" fake id nếu đã có một cái đang mở =====
    const hasFakeNewChat = selectedListUser.some(
      p => p.id === "00000000-0000-0000-0000-000000000000"
    );

    // Chỉ chặn khi đang tạo NEW chat, không phải khi click vào group đã có id thật
    if (!reciver && hasFakeNewChat) {
      return;
    }

    // ===== 3. Logic tạo mới như cũ =====
    let newSelectedListUser: IConversationReq[];

    if (reciver?.type === TagType.Conversation) {
      const page: IGetPageConversationReq = {
        conversationId: reciver.id,
        currentPage: 1,
        pageSize: 20
      };
      const res = await ConversationService.getPageMessages(page);
      const history = res?.items?.reverse() || [];

      const newGroupChat: IConversationReq = {
        id: reciver.id,
        title: reciver.fullName,
        type: type,
        isOpenMessage: true,
        isCreateGroupChat: false,
        participantIds: [],
        messages: history,
        participants: []
      };
      newSelectedListUser = [...selectedListUser, newGroupChat];
    } else {
      const newGroupChat: IConversationReq = {
        id: "00000000-0000-0000-0000-000000000000",
        title: t("Chat.GroupNew"),
        type: type,
        isOpenMessage: true,
        isCreateGroupChat: true,
        participantIds: [],
        messages: [],
        participants: []
      };
      newSelectedListUser = [...selectedListUser, newGroupChat];
    }

    // Set mặc định receiver cho Direct / Group khi có reciver
    if (type == TypeConversation.Group || isUser) {
      if (reciver) {
        let newItag: ITag = {
          id: reciver?.id,
          fullName: reciver?.fullName,
          userName: reciver.userName ?? reciver?.fullName,
          avatar: reciver.avatar,
          type: reciver.type ?? 0
        };
        setValue("receiver", [{ ...newItag }]);
      }
    }

    // Ẩn bớt tab nếu >2 panel
    if (selectedListUser.length > 2) {
      let found = false;
      newSelectedListUser = newSelectedListUser.map(p => {
        if (!found && p.isHiddenChat !== true) {
          found = true;
          return { ...p, isHiddenChat: true };
        }
        return p;
      });
    }

    setSelectedListUser(newSelectedListUser);

    setTimeout(() => reciver && scrollToBottom(reciver.id), 10);
    handleCloseMenuMessage();
  };

  const handRemoveSelectedUser = (user: IConversationReq) => {
    const indexUser = selectedListUser.findIndex(p => p.id === user.id);
    if (indexUser > -1) {
      let newSelectedListUser = [
        ...selectedListUser.slice(0, indexUser),
        ...selectedListUser.slice(indexUser + 1)
      ];

      // ✅ Chỉ thực hiện nếu còn lại ít hơn 3 phần tử
      if (newSelectedListUser.length > 2) {
        let found = false;
        newSelectedListUser = newSelectedListUser.map(p => {
          if (!found && p.isHiddenChat === true) {
            found = true;
            return { ...p, isHiddenChat: false }; // 👉 Gỡ ẩn phần tử đầu tiên bị ẩn
          }
          return p;
        });
      }

      setSelectedListUser(newSelectedListUser);
      setValue("receiver", []);
    }
  };

  const handMinusGroup = (user: IConversationReq) => {
    const newSelectedListUser = selectedListUser.map(p => {
      if (p.id === user.id) {
        return { ...p, isOpenMessage: false }; // tạo bản sao mới
      }
      return p;
    });

    setSelectedListUser(newSelectedListUser);
  };

  const handleOpenGroup = (user: IConversationReq) => {
    const newSelectedListUser = selectedListUser.map(p => {
      if (p.id === user.id) {
        p.isOpenMessage = true;
      }

      return p;
    });

    setSelectedListUser(newSelectedListUser);
  };

  const handleOpenEditGroupChat = (conv?: IConversationReq) => {
    conv && conv.id && setDataUpdateTitle({ id: conv.id, title: conv.title });

    setIsOpenModelEditNameGroupChat(prev => !prev); // 🔁 đảo trạng thái open/close
    handleCloseMenuMessage();
  };

  const handleScroll = (
    e: React.UIEvent<HTMLDivElement>,
    groupId: string | undefined
  ) => {
    const element = e.currentTarget;
    if (element.scrollTop === 0) {
      setSelectedListUser(prev =>
        prev.map(g =>
          g.id === groupId
            ? { ...g, pageSize: (g.pageSize || 20) + 10 } // 👈 tăng riêng cho group đó
            : g
        )
      );
    }
  };

  useEffect(() => {
    selectedListUser.forEach(async g => {
      if (g.pageSize && g.id) {
        const page: IGetPageConversationReq = {
          conversationId: g.id,
          currentPage: 1,
          pageSize: g.pageSize
        };
        const res = await ConversationService.getPageMessages(page);

        if (res?.items) {
          setSelectedListUser(prev =>
            prev.map(x =>
              x.id === g.id ? { ...x, messages: res.items.reverse() } : x
            )
          );
        }
      }
    });
  }, [selectedListUser.map(g => g.pageSize).join(",")]); // theo dõi khi pageSize thay đổi

  const validationSchema = Yup.object().shape({
    // workflowName: Yup.string()
    //   .trim()
    //   .required(`${"Vui lòng nhập tên luồng công việc"}`)
  }) as any;

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`
    };
  };

  const onChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue == 1) getChatUsers();

    setTabValue(newValue);
  };

  const getChatUsers = async (value?: string) => {
    if (value) {
      setSearchValue(value);
      setTabValue(1);
    } else {
      setTabValue(0);
      setSearchValue("");
    }

    const pageUser: ITagReq = {
      CurrenPage: 1,
      PageSize: 10,
      KeySearch: value ? value : "",
      Type: TagType.User
    };

    const users = await UserService.getPageChatUserOrDepartment(pageUser);

    setUserChats(users);
  };

  const handleScrollConversation = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const nearBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 1;

    if (nearBottom && !isLoading && hasMore) {
      loadData(pageSizeConversation + 10); // ❗️mỗi lần tăng thêm 10
    }
  };

  // Khi chọn file
  const handleFileChange = (
    group: IConversationReq,
    e?: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e?.target.files) {
      const files = Array.from(e.target.files);
      setSelectedListUser(prev =>
        prev.map(g =>
          g.id === group.id
            ? {
                ...g,
                selectedFiles: [...(g.selectedFiles || []), ...files] // ✅ giữ nguyên file cũ, thêm file mới
              }
            : g
        )
      );
    }

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // Khi xoá file
  const handleRemoveFile = (group: IConversationReq, index: number) => {
    setSelectedListUser(prev =>
      prev.map(g =>
        g.id === group.id
          ? {
              ...g,
              selectedFiles: (g.selectedFiles || []).filter(
                (_, i) => i !== index
              )
            }
          : g
      )
    );
  };
  const handleEmojiClick = (gr: IConversationReq, emojiData: any) => {
    const emoji = emojiData.emoji;
    setSelectedListUser(prev =>
      prev.map(g =>
        g.id === gr.id ? { ...g, text: (g.text || "") + emoji } : g
      )
    );
  };

  // Chuẩn hoá để so sánh không dấu
  const norm = (s?: string) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // Lấy danh sách gợi ý theo gr + query
  const getFilteredMentionList = (gr: IConversationReq) => {
    const q = norm(mentionQuery);
    const list = (gr.participants || []).filter(u => {
      const fn = norm(u.fullName);
      const un = norm(u.userName);
      if (!q) return true; // @ + rỗng => show tất cả
      return fn.includes(q) || un.includes(q);
    });
    return list;
  };

  const handleSelectUser = (gr: IConversationReq, user: any) => {
    const text = gr.text || "";
    const atPos = text.lastIndexOf("@", cursorPos - 1);
    if (atPos === -1) return;

    const startText = text.slice(0, atPos);
    const endText = text.slice(cursorPos);
    const mentionText = `@${user.fullName} `;
    const newMessage = startText + mentionText + endText;

    setSelectedListUser(prev =>
      prev.map(g => (g.id === gr.id ? { ...g, text: newMessage } : g))
    );

    setShowMentionList(false);
    setMentionIndex(0);
    setMentionQuery(""); // 👈 reset luôn query

    setTimeout(() => {
      if (inputRef.current) {
        const newCaretPos = (startText + mentionText).length;
        inputRef.current.setSelectionRange(newCaretPos, newCaretPos);
        inputRef.current.focus();
      }
    }, 0);
  };

  const onSubmit = async (
    data: DataForm,
    gr: IConversationReq,
    inputValue?: string
  ) => {
    gr.participantIds = data.receiver.map(p => p.id);
    await handleSend(gr, inputValue);
  };

  const {
    control,
    reset,
    resetField,
    getValues,
    setValue,
    register,
    handleSubmit,
    trigger,
    formState: { errors }
  } = useForm<DataForm>({
    resolver: yupResolver(validationSchema)
  });

  return (
    <>
      <Wrapper>
        <Sidebar>
          <Header>
            {isSearchOpen ? (
              <>
                <SearchIcon
                  size={18}
                  style={{ cursor: "pointer", opacity: 0.9 }}
                />
                <Box
                  //px={2}
                  //py={1}
                  sx={{
                    // borderBottom: "1px solid #e0e0e0",
                    //backgroundColor: "#fff",
                    animation: "fadeIn 0.2s ease"
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={t("Chat.SearchForChatGroup") + "..."}
                    value={searchValue}
                    onChange={e => getChatUsers(e.target.value)}
                    sx={{
                      border: "none",
                      padding: "0 7px",
                      "& input": {
                        // padding: "0 14px",
                        color: "#ffffff",
                        fontSize: "14px"
                        // fontWeight: 600
                      },
                      "& fieldset": {
                        border: "none"
                      },
                      "& input::placeholder": {
                        fontWeight: 600, // 👉 chỉnh độ đậm
                        fontSize: 14, // tùy chọn
                        color: "#ffffff", // tùy chọn
                        opacity: 1 // tránh bị mờ
                      },
                      "& .MuiOutlinedInput-root": {
                        //borderRadius: "20px",

                        backgroundColor: variableStyles.MainColor
                        // padding: 0
                      }
                    }}
                  />
                </Box>

                <X
                  onClick={() => setIsSearchOpen(prev => !prev)}
                  size={18}
                  style={{ cursor: "pointer", opacity: 0.9 }}
                />
              </>
            ) : (
              <>
                <Typography
                  onClick={() => setIsOpenMessage(true)}
                  variant="body2"
                  sx={{ fontWeight: 600, cursor: "pointer" }}
                >
                  {companyName}
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <Search
                    onClick={() => setIsSearchOpen(prev => !prev)}
                    size={18}
                    style={{ cursor: "pointer", opacity: 0.9 }}
                  />

                  {!isOpenMessage && (
                    <Plus
                      onClick={() => setIsOpenMessage(true)}
                      size={18}
                      style={{ cursor: "pointer", opacity: 0.9 }}
                    />
                  )}
                  {isOpenMessage && (
                    <Minus
                      onClick={() => setIsOpenMessage(false)}
                      size={18}
                      style={{ cursor: "pointer", opacity: 0.9 }}
                    />
                  )}
                </Box>
              </>
            )}
          </Header>
          {/* Ô tìm kiếm (ẩn/hiện khi click Search) */}

          <Box sx={{ display: isOpenMessage ? "block" : "none" }}>
            <Tabs
              value={tabValue}
              onChange={onChangeTab}
              aria-label="basic tabs example"
              TabIndicatorProps={{ style: { display: "none" } }}
            >
              <Tab
                sx={{
                  padding: "4px 0"
                }}
                {...a11yProps(0)}
                label={
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600, color: "#d9534f" }}
                  >
                    {t("Chat.Messages")}
                  </Typography>
                }
              />

              <Tab
                sx={{
                  padding: "4px 0"
                }}
                {...a11yProps(1)}
                label={
                  <Box alignItems="center" display={"flex"}>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ color: "green", fontSize: 14 }}
                    >
                      <span>●</span>
                      <span>Online</span>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: 4,
                        px: 1,
                        fontSize: 14,
                        fontWeight: 500
                      }}
                    >
                      {totalUserOnline}
                    </Box>
                  </Box>
                }
              />

              {/* ✅ Tab giả (không thay đổi tabValue) */}
              <Tab
                {...a11yProps(2)}
                sx={{ padding: "4px 0" }}
                label={
                  <Box display="flex" alignItems="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{
                        fontSize: 14,
                        color: variableStyles.MainColor,
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                    >
                      + {t("Chat.NewChat")}
                    </Box>
                  </Box>
                }
                onClick={e => {
                  e.stopPropagation(); // ⛔ không trigger Tabs onChange
                  handCreateGroupChat(TypeConversation.Direct);
                }}
              />
            </Tabs>

            {/* <TabBar>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: "#d9534f" }}
              >
                Tin nhắn
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <OnlineBox>
                  <span>●</span>
                  <span>Online</span>
                </OnlineBox>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: 4,
                    px: 1,
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  {totalUserOnline}
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  fontSize: 14,
                  color: variableStyles.MainColor,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
                onClick={() => handCreateGroupChat(TypeConversation.Direct)}
              >
                + Mới
              </Box>
            </TabBar> */}

            <CustomChatTabPanel value={tabValue} index={0}>
              <UserList onScroll={handleScrollConversation}>
                {conversation?.items?.map(cv => (
                  <UserItem
                    key={cv.id}
                    // ref={el => cv.id && (messagesRef.current[cv.id] = el)}
                    //selected={selectedUser?.id === user.id}
                    onClick={() => handSelectedConversation(cv)}
                  >
                    <Badge
                      color="success"
                      overlap="circular"
                      variant={cv.online ? "dot" : "standard"}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      // sx={{
                      //   backgroundColor: variableStyles.MainColor
                      // }}
                    >
                      <AvatarBox
                        width={40}
                        height={40}
                        src={
                          cv.avatar
                            ? envConfig.MEDIA_ENDPOINT + cv.avatar
                            : undefined
                        }
                        sx={{
                          backgroundColor: variableStyles.MainColor // fallback nếu chưa có ảnh
                        }}
                      >
                        {!cv.avatar && cv.title.charAt(0)}{" "}
                        {/* fallback chữ cái đầu */}
                      </AvatarBox>
                    </Badge>

                    <MessageBox>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography fontSize={14} fontWeight={500}>
                          {cv.title}
                        </Typography>
                        {(cv.unreadCount ?? 0) > 0 && (
                          <Box
                            sx={{
                              color: "#ffffff",
                              backgroundColor: "red",
                              width: "20px",
                              height: "20px",
                              textAlign: "center",
                              borderRadius: "10px",
                              fontSize: "14px"
                            }}
                          >
                            {cv.unreadCount}
                          </Box>
                        )}
                        {/* {cv.unread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "red"
                          }}
                        />
                      )} */}
                      </Box>
                      <Typography
                        fontSize={12}
                        color="gray"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 200
                        }}
                      >
                        {/* {user.message} */}
                      </Typography>
                    </MessageBox>
                  </UserItem>
                ))}
                {isLoading && (
                  <Box textAlign="center" py={2}>
                    <Typography variant="body2" color="gray">
                      {t("Common.LoadingMore")}...
                    </Typography>
                  </Box>
                )}
              </UserList>
            </CustomChatTabPanel>
            <CustomChatTabPanel value={tabValue} index={1}>
              <UserList>
                {userChats?.items?.map(cv => (
                  <UserItem
                    key={cv.id}
                    //selected={selectedUser?.id === user.id}
                    onClick={() =>
                      handCreateGroupChat(TypeConversation.Direct, cv, true)
                    }
                  >
                    <Badge
                      color="success"
                      overlap="circular"
                      //variant={cv.online ? "dot" : "standard"}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      // sx={{
                      //   backgroundColor: variableStyles.MainColor
                      // }}
                    >
                      <AvatarBox
                        width={40}
                        height={40}
                        src={
                          cv.avatar
                            ? envConfig.MEDIA_ENDPOINT + cv.avatar
                            : undefined
                        }
                        sx={{
                          backgroundColor: variableStyles.MainColor // fallback nếu chưa có ảnh
                        }}
                      >
                        {!cv.avatar && cv.fullName?.trim().charAt(0)}{" "}
                        {/* fallback chữ cái đầu */}
                      </AvatarBox>
                    </Badge>

                    <MessageBox>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography fontSize={14} fontWeight={500}>
                          {cv.fullName}
                        </Typography>
                        {/* {cv.unread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "red"
                          }}
                        />
                      )} */}
                      </Box>
                      <Typography
                        fontSize={12}
                        color="gray"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 200
                        }}
                      >
                        {/* {user.message} */}
                      </Typography>
                    </MessageBox>
                  </UserItem>
                ))}
              </UserList>
            </CustomChatTabPanel>
            <CustomChatTabPanel value={tabValue} index={2}>
              <UserList>
                {userChats?.items?.map(cv => (
                  <UserItem
                    key={cv.id}
                    //selected={selectedUser?.id === user.id}
                    onClick={() =>
                      handCreateGroupChat(TypeConversation.Direct, cv, true)
                    }
                  >
                    <Badge
                      color="success"
                      overlap="circular"
                      //variant={cv.online ? "dot" : "standard"}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      // sx={{
                      //   backgroundColor: variableStyles.MainColor
                      // }}
                    >
                      <AvatarBox
                        width={40}
                        height={40}
                        src={
                          cv.avatar
                            ? envConfig.MEDIA_ENDPOINT + cv.avatar
                            : undefined
                        }
                        sx={{
                          backgroundColor: variableStyles.MainColor // fallback nếu chưa có ảnh
                        }}
                      >
                        {!cv.avatar && cv.fullName?.trim().charAt(0)}{" "}
                        {/* fallback chữ cái đầu */}
                      </AvatarBox>
                    </Badge>

                    <MessageBox>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography fontSize={14} fontWeight={500}>
                          {cv.fullName}
                        </Typography>
                        {/* {cv.unread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "red"
                          }}
                        />
                      )} */}
                      </Box>
                      <Typography
                        fontSize={12}
                        color="gray"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 200
                        }}
                      >
                        {/* {user.message} */}
                      </Typography>
                    </MessageBox>
                  </UserItem>
                ))}
              </UserList>
            </CustomChatTabPanel>
          </Box>
        </Sidebar>

        {selectedListUser &&
          selectedListUser.map((gr, index) => {
            // Giả sử mỗi gr có: { id, name, isGroup, messages, input }
            //nst messages = gr.messages || [];
            const inputValue = "";
            const isGroup = gr.type;

            return (
              <>
                <ChatPanel
                  key={gr.id}
                  sx={{
                    width: "max(278px)",
                    display: gr.isHiddenChat ? "none" : "block"
                  }}
                >
                  {/* Header */}
                  <ChatHeader
                    sx={{
                      borderBottom: "1px solid #e0e0e0",
                      background: "#f9f9f9",
                      py: 1,
                      px: 2
                      // width: "max(100%, 385px)"
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box
                        display="flex"
                        gap={1}
                        sx={{
                          alignItems: "center"
                        }}
                      >
                        <Box
                          display="flex"
                          gap={1}
                          onClick={() => handleOpenGroup(gr)}
                          sx={{ cursor: "pointer" }}
                        >
                          {gr.online && (
                            <OnlineBox>
                              <span>●</span>
                            </OnlineBox>
                          )}

                          <Typography
                            sx={{
                              maxWidth: "120px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                            fontSize="14px"
                            fontWeight={600}
                          >
                            {gr.title}
                          </Typography>
                          {!gr.isCreateGroupChat && (
                            <div
                              onClick={event =>
                                gr.id && handleClick(event, gr.id)
                              }
                              style={{
                                cursor: "pointer",
                                opacity: 0.9,
                                margin: "2px 0px 0px 0px"
                              }}
                            >
                              <ChevronDown size={18} />
                            </div>
                          )}
                        </Box>

                        {/* Menu con nổi */}
                        <Menu
                          anchorEl={menuState.anchorEl}
                          open={menuState.conversationId == gr.id}
                          onClose={() => handleCloseMenuMessage()}
                          //  sx={{ left: "1111px" }}
                          PaperProps={{
                            sx: {
                              bgcolor: "#1e1f22",
                              color: "#fff",
                              borderRadius: 2,
                              minWidth: 220,
                              mt: 1,
                              boxShadow: "0 4px 20px rgba(0,0,0,0.4)"
                            }
                          }}
                          transformOrigin={{
                            horizontal: "right",
                            vertical: "top"
                          }}
                          anchorOrigin={{
                            horizontal: "right",
                            vertical: "bottom"
                          }}
                        >
                          {gr.type == TypeConversation.Group && (
                            <MenuItem
                              onClick={() => handleOpenEditGroupChat(gr)}
                            >
                              <ListItemIcon>
                                <Pencil size={16} color="#ccc" />
                              </ListItemIcon>
                              <ListItemText
                                primary={t("Chat.ConversationName")}
                              />
                            </MenuItem>
                          )}
                          {gr.type == TypeConversation.Direct && (
                            <MenuItem
                              onClick={() =>
                                handCreateGroupChat(
                                  TypeConversation.Group,
                                  gr.reciver
                                )
                              }
                            >
                              <ListItemIcon>
                                <Users size={16} color="#ccc" />
                              </ListItemIcon>
                              <ListItemText primary={t("Chat.CreateGroup")} />
                            </MenuItem>
                          )}
                        </Menu>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        {(gr.unreadCount ?? 0) > 0 && (
                          <Box
                            sx={{
                              color: "#ffffff",
                              backgroundColor: "red",
                              width: "20px",
                              height: "20px",
                              textAlign: "center",
                              borderRadius: "10px",
                              fontSize: "14px"
                            }}
                          >
                            {gr.unreadCount}
                          </Box>
                        )}
                        <Minus
                          onClick={() => handMinusGroup(gr)}
                          size={18}
                          style={{
                            cursor: "pointer",
                            opacity: 0.9,
                            display: gr.isOpenMessage ? "block" : "none"
                          }}
                        />

                        <X
                          onClick={() => handRemoveSelectedUser(gr)}
                          size={18}
                          style={{ cursor: "pointer", opacity: 0.9 }}
                        />
                      </Box>
                    </Box>
                  </ChatHeader>

                  <Box
                    sx={{
                      display: gr.isOpenMessage ? "block" : "none"
                    }}
                  >
                    <Box
                      sx={{
                        display: gr.isCreateGroupChat ? "block" : "none"
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          borderBottom: "1px solid #e0e0e0"
                        }}
                      >
                        <Box sx={{ paddingLeft: "10px" }}>
                          {t("Common.To")}:{" "}
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            "& fieldset": {
                              border: "none"
                            }
                          }}
                        >
                          <TagInput
                            name={"receiver"}
                            control={control}
                            errors={errors}
                            //defaultValues={getValues("management")}
                            isError={!!errors.receiver}
                            placeholder={""}
                            type={TagType.User}
                            limit={gr.type == TypeConversation.Direct ? 1 : 100}
                          />
                        </Box>

                        {/* <Autocomplete
                          multiple
                          limitTags={2}
                          id="size-small-standard-multi"
                          size="small"
                          options={top100Films}
                          getOptionLabel={option => option.title}
                          defaultValue={[top100Films[13]]}
                          sx={{
                            padding: "5px 0",
                            width: "100%",
                            "& .MuiAutocomplete-inputRoot": {
                              fontSize: "13px"
                            }, // input
                            "& .MuiAutocomplete-option": { fontSize: "13px" }, // item trong dropdown
                            "& .MuiChip-label": { fontSize: "13px" } // tag
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="standard"
                              //label="Size small"
                              placeholder="Favorites"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true // 🔥 bỏ gạch chân variant=standard
                              }}
                              sx={{}}
                            />
                          )}
                        /> */}
                      </Box>
                    </Box>

                    {/* Danh sách tin nhắn */}
                    <ChatMessages
                      key={gr.id}
                      ref={el => gr.id && (messagesRef.current[gr.id] = el)}
                      onScroll={e => handleScroll(e, gr.id)}
                      sx={{
                        flex: 1,
                        overflowY: "auto",
                        p: 2,
                        backgroundColor: "#f5f7fa",
                        height: "286px"
                      }}
                    >
                      {gr.messages?.length === 0 && (
                        <Typography
                          fontSize={13}
                          color="gray"
                          textAlign="center"
                          mt={2}
                        >
                          {t("Chat.NoMessagesYet")}.
                        </Typography>
                      )}
                      {gr.messages?.map((msg, index) => {
                        const isMe = msg.senderId === token?.userId;
                        const isLastMessage =
                          gr.messages && index === gr.messages.length - 1; // 👈 kiểm tra tin cuối

                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              flexDirection: isMe ? "row-reverse" : "row",
                              alignItems: "center",
                              mb: 1.8
                            }}
                          >
                            {/* Avatar: chỉ hiện nếu KHÔNG phải tôi */}
                            {!isMe && (
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 13,
                                  bgcolor: variableStyles.MainColor,
                                  mr: 1
                                }}
                                src={
                                  envConfig.MEDIA_ENDPOINT + msg.sender?.avatar
                                }
                              >
                                {msg.sender.fullName
                                  ?.trim()
                                  .charAt(0)
                                  .toUpperCase() ?? "?"}
                              </Avatar>
                            )}

                            {/* Bong bóng + tên */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: isMe ? "flex-end" : "flex-start",
                                width: "100%",
                                maxWidth: "65%"
                              }}
                            >
                              {/* Tên người gửi (nếu nhóm và không phải tôi) */}
                              {isGroup == TypeConversation.Group && !isMe && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: 12,
                                    color: "#555",
                                    ml: 0.5,
                                    mb: 0.3
                                  }}
                                >
                                  {msg?.sender?.fullName ?? ""}
                                </Typography>
                              )}

                              {/* Bong bóng chat */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  justifyContent: isMe ? "end" : "start"
                                }}
                              >
                                {/* Hiển thị file đính kèm */}
                                {msg.attachments?.length > 0 && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      display: "flex",
                                      //flexDirection: "column",
                                      gap: 1,
                                      width: "100%",
                                      justifyContent: isMe ? "end" : "start",
                                      flexDirection: "column"
                                    }}
                                  >
                                    {msg.attachments.map((file, idx) => {
                                      const isImage =
                                        file.contentType.startsWith("image/");
                                      const isPdf = file.extension === ".pdf";
                                      const isDoc = [
                                        ".doc",
                                        ".docx",
                                        ".xls",
                                        ".xlsx",
                                        ".txt"
                                      ].includes(file.extension);

                                      return (
                                        <Box
                                          key={file.id || idx}
                                          sx={{
                                            borderRadius: "8px",
                                            overflow: "hidden",
                                            backgroundColor: isMe
                                              ? "rgb(204 204 204 / 20%)"
                                              : "#f1f1f1",
                                            p: 1,
                                            cursor: "pointer",
                                            "&:hover": { opacity: 0.8 }
                                          }}
                                          onClick={() =>
                                            window.open(
                                              envConfig.MEDIA_ENDPOINT +
                                                file.path,
                                              "_blank"
                                            )
                                          }
                                        >
                                          {/* Ảnh */}
                                          {isImage && (
                                            <Box
                                              component="img"
                                              src={
                                                envConfig.MEDIA_ENDPOINT +
                                                file.path
                                              }
                                              alt={file.fileName}
                                              sx={{
                                                width: "150px",
                                                height: "auto",
                                                borderRadius: "8px",
                                                display: "block"
                                              }}
                                            />
                                          )}

                                          {/* PDF */}
                                          {isPdf && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1
                                              }}
                                            >
                                              <PictureAsPdf
                                                sx={{ color: "red" }}
                                              />
                                              <Typography fontSize={13}>
                                                {file.fileName}
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Tài liệu hoặc file khác */}
                                          {!isImage && !isPdf && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1
                                              }}
                                            >
                                              <AttachFile
                                                sx={{ color: "#1976d2" }}
                                              />
                                              <Typography fontSize={13}>
                                                {file.fileName}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}

                                <Box
                                  sx={{
                                    backgroundColor: isMe
                                      ? "#05224e"
                                      : "#f0f0f0",
                                    color: isMe ? "#fff" : "#000",
                                    borderRadius: "18px",
                                    px: 1.6,
                                    py: 1,
                                    width: "fit-content",
                                    //maxWidth: "65%",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                                    wordWrap: "break-word"
                                  }}
                                >
                                  {/* Nội dung tin nhắn */}
                                  {msg.content && (
                                    <RenderMentions
                                      isMe
                                      content={msg.content.replace(
                                        /<[^>]+>/g,
                                        ""
                                      )}
                                      participants={gr.participants || []}
                                    />
                                  )}

                                  {/* Thời gian gửi */}
                                  <Typography
                                    fontSize={10}
                                    color={isMe ? "#e3f2fd" : "gray"}
                                    textAlign="right"
                                    mt={0.5}
                                  >
                                    {new Date(
                                      msg.createdDate
                                    ).toLocaleTimeString("vi-VN", {
                                      hour: "2-digit",
                                      minute: "2-digit"
                                    })}
                                  </Typography>
                                </Box>
                              </Box>

                              <Badge
                                color="success"
                                overlap="circular"
                                //variant={msg. ? "dot" : "standard"}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right"
                                }}
                              >
                                {isMe &&
                                  isLastMessage &&
                                  msg.seenBy &&
                                  msg.seenBy.map((x, i) => (
                                    <Tooltip
                                      key={x.id ?? i}
                                      title={`${x.fullName ?? t("Common.Undefined")} ${t("Chat.SeenAt")} ${
                                        x.seenAt
                                          ? new Date(
                                              x.seenAt
                                            ).toLocaleTimeString("vi-VN", {
                                              hour: "2-digit",
                                              minute: "2-digit"
                                            })
                                          : t("Common.Undefined")
                                      }`}
                                      arrow
                                      placement="top"
                                    >
                                      <AvatarBox
                                        width={15}
                                        height={15}
                                        src={
                                          x.avatar
                                            ? envConfig.MEDIA_ENDPOINT +
                                              x.avatar
                                            : undefined
                                        }
                                        sx={{
                                          backgroundColor:
                                            variableStyles.MainColor // fallback nếu chưa có ảnh
                                        }}
                                      >
                                        {!x.avatar &&
                                          x.fullName?.trim().charAt(0)}{" "}
                                        {/* fallback chữ cái đầu */}
                                      </AvatarBox>
                                    </Tooltip>
                                  ))}
                              </Badge>
                            </Box>
                          </Box>
                        );
                      })}
                      <div id={`bottom-${gr.id}`} />

                      {/* dummy bottom if needed */}
                    </ChatMessages>

                    {/* Ô nhập tin nhắn */}
                    <ChatInputBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: "1px solid #e0e0e0",
                        px: 2,
                        py: 1,
                        background: "#fff",
                        position: "relative",
                        paddingLeft: 0,
                        paddingRight: 0,
                        flexDirection: "column"
                      }}
                    >
                      {/* ✅ Danh sách file đính kèm hiển thị trên ô input */}
                      {gr?.selectedFiles && gr?.selectedFiles.length > 0 && (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            p: 1,
                            pt: 0,
                            pb: 0.5,
                            overflowY: "auto",
                            maxHeight: 120
                          }}
                        >
                          {gr?.selectedFiles?.map((file, index) => {
                            const isImage = file.type.startsWith("image/");
                            return (
                              <Box
                                key={index}
                                sx={{
                                  position: "relative",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  border: "1px solid #ddd",
                                  width: isImage ? 80 : "auto",
                                  height: isImage ? 80 : "auto",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f9f9f9",
                                  px: isImage ? 0 : 1
                                }}
                              >
                                {isImage ? (
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover"
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2">
                                    {file.name}
                                  </Typography>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveFile(gr, index)}
                                  sx={{
                                    position: "absolute",
                                    top: 2,
                                    right: 2,
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.9)"
                                    }
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {/* Dòng chứa input, emoji, gửi... */}
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center"
                        }}
                      >
                        {/* Nút đính kèm file */}
                        <IconButton color="primary" component="label">
                          <AttachFile sx={{ fontSize: 20 }} />
                          <input
                            type="file"
                            hidden
                            multiple
                            onChange={e => {
                              handleFileChange(gr, e);
                            }}
                          />
                        </IconButton>

                        {/* Ô nhập tin nhắn có emoji bên trong */}
                        <Box
                          sx={{ flexGrow: 1, position: "relative" }}
                          ref={emojiInputRef}
                        >
                          {/* Popup Mention */}
                          {showMentionList &&
                            gr.type === TypeConversation.Group &&
                            gr.participants &&
                            gr.participants.length > 0 && (
                              <Box
                                ref={mentionRef}
                                sx={{
                                  position: "absolute",
                                  bottom: "45px",
                                  left: "0px",
                                  zIndex: 20,
                                  bgcolor: "#fff",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  borderRadius: 1,
                                  maxHeight: 200,
                                  overflowY: "auto",
                                  width: 250
                                }}
                              >
                                {gr.participants
                                  .filter(
                                    u =>
                                      u.fullName
                                        ?.toLowerCase()
                                        .includes(mentionQuery) ||
                                      u.userName
                                        ?.toLowerCase()
                                        .includes(mentionQuery)
                                  )
                                  .map(u => (
                                    <Box
                                      key={u.id}
                                      onClick={() => handleSelectUser(gr, u)}
                                      sx={{
                                        px: 1.5,
                                        py: 1,
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: "#f5f5f5"
                                        }
                                      }}
                                    >
                                      <Avatar
                                        src={
                                          u.avatar
                                            ? envConfig.MEDIA_ENDPOINT +
                                              u.avatar
                                            : undefined
                                        }
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          fontSize: 12,
                                          backgroundColor:
                                            variableStyles.MainColor
                                        }}
                                      >
                                        {!u.avatar &&
                                          (u.fullName?.charAt(0) ?? "?")}
                                      </Avatar>
                                      <Typography>{u.fullName}</Typography>
                                    </Box>
                                  ))}
                              </Box>
                            )}
                          <TextField
                            inputRef={inputRef}
                            fullWidth
                            size="small"
                            placeholder={t("Chat.TypeYourMessage") + "..."}
                            autoComplete="off"
                            value={gr.text}
                            onFocus={() => gr.id && handleInputFocus(gr.id)}
                            onChange={e =>
                              gr.id && handleInputChange(gr.id, e.target.value)
                            }
                            onKeyDown={e => {
                              const hasFiles =
                                (gr.selectedFiles?.length || 0) > 0;

                              // ====== Điều khiển danh sách @mention bằng phím ======
                              if (
                                showMentionList &&
                                gr.type === TypeConversation.Group
                              ) {
                                const list = getFilteredMentionList(gr);
                                const isTypingMention =
                                  (mentionQuery || "").trim().length > 0;

                                // 👉 Nếu đang có file đính kèm thì Enter = GỬI TIN, KHÔNG chọn mention
                                if (
                                  hasFiles &&
                                  e.key === "Enter" &&
                                  !e.shiftKey
                                ) {
                                  e.preventDefault();
                                  if (gr.isCreateGroupChat) {
                                    handleSubmit(data =>
                                      onSubmit(data, gr, gr.text)
                                    )();
                                  } else {
                                    handleSend(gr, gr.text);
                                  }
                                  return;
                                }

                                // 👉 Chỉ dùng Enter để chọn mention khi ĐANG GÕ TÊN (có query)
                                if (list.length > 0 && isTypingMention) {
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setMentionIndex(
                                      prev => (prev + 1) % list.length
                                    );
                                    return;
                                  }
                                  if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setMentionIndex(
                                      prev =>
                                        (prev - 1 + list.length) % list.length
                                    );
                                    return;
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const pick =
                                      list.length === 1
                                        ? list[0]
                                        : list[mentionIndex] || list[0];
                                    handleSelectUser(gr, pick);
                                    return;
                                  }
                                  if (e.key === "Escape") {
                                    setShowMentionList(false);
                                    return;
                                  }
                                }
                                // Nếu không gõ tên hoặc không có list → không chặn Enter, cho xuống phần gửi tin
                              }

                              // ====== Gửi tin nhắn ======
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (gr.isCreateGroupChat) {
                                  handleSubmit(data =>
                                    onSubmit(data, gr, gr.text)
                                  )();
                                } else {
                                  handleSend(gr, gr.text);
                                }
                              }
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                backgroundColor: "#f1f1f1",
                                fontSize: "14px",
                                "& fieldset": { border: "none" },
                                paddingRight: 0
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    color="primary"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setOpenEmojiFor(
                                        prev =>
                                          (prev === gr.id ? null : gr.id) as
                                            | string
                                            | null
                                      );
                                    }}
                                  >
                                    <InsertEmoticon sx={{ fontSize: 22 }} />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />

                          {openEmojiFor == gr.id && (
                            <Box
                              ref={emojiPopupRef}
                              sx={{
                                position: "absolute",
                                bottom: "45px",
                                right: "10px",
                                zIndex: 10,
                                boxShadow: "0 0 10px rgba(0,0,0,0.2)"
                              }}
                            >
                              <EmojiPicker
                                onEmojiClick={data =>
                                  handleEmojiClick(gr, data)
                                }
                                width={300}
                                height={350}
                                previewConfig={{ showPreview: false }}
                              />
                            </Box>
                          )}
                        </Box>

                        {/* Nút gửi */}
                        <IconButton
                          color="primary"
                          onClick={
                            gr.isCreateGroupChat
                              ? handleSubmit(data =>
                                  onSubmit(data, gr, gr.text)
                                )
                              : () => handleSend(gr, gr.text)
                          }
                        >
                          <Send size={20} />
                        </IconButton>
                      </Box>
                    </ChatInputBox>
                  </Box>
                </ChatPanel>
              </>
            );
          })}
      </Wrapper>

      <EditNameGroupChat
        open={isOpenModelEditNameGroupChat}
        handleClose={() => handleOpenEditGroupChat()}
        data={dataUpdateTitle}
      />
    </>
  );
};

export default ChatSidebar;
